/**
 * Stock CSV export/import column order and cell formatting for round-trip editing.
 */
import dayjs from "dayjs";
import { escapeCsvCell } from "./csvFileHelpers";
import { formatStockPctForDisplayExport } from "./stockPercentFormat";

/** Column order matches sample CSV and import aliases. */
export const STOCK_CSV_COLUMN_IDS = [
  "symbol",
  "name",
  "earnings_date",
  "pct_change",
  "pct_change_5d",
  "pct_10d_ma",
  "pct_50d_ma",
  "pct_change_1m",
  "pct_change_3m",
  "pct_change_52w",
  "pct_52w_high",
  "pct_52w_low",
  "price_sales",
  "market_cap",
  "analyst_rating",
  "short_float",
  "pe_fwd",
  "pe_ttm",
  "sector",
  "industry",
  "equity",
];

export const STOCK_CSV_HEADER_LINE = STOCK_CSV_COLUMN_IDS.join(",");

const STOCK_CSV_PCT_FIELDS = new Set([
  "pct_change",
  "pct_change_5d",
  "pct_10d_ma",
  "pct_50d_ma",
  "pct_change_1m",
  "pct_change_3m",
  "pct_change_52w",
  "pct_52w_high",
  "pct_52w_low",
  "short_float",
]);

const STOCK_CSV_TEXT_FIELDS = new Set(["name", "analyst_rating", "sector", "industry"]);

/** @param {unknown} value */
export function formatStockPctForCsv(value) {
  return formatStockPctForDisplayExport(value);
}

/** @param {unknown} value */
export function formatStockEarningsDateForCsv(value) {
  if (!value) return "";
  const d = dayjs(value);
  return d.isValid() ? d.format("M/D/YYYY") : "";
}

/** @param {unknown} value */
export function formatStockNumericForCsv(value) {
  if (value == null || value === "") return "";
  const n = Number(value);
  if (!Number.isFinite(n)) return "";
  return String(n);
}

/**
 * @param {Record<string, unknown>} stock
 * @returns {string[]}
 */
export function stockRecordToCsvCells(stock) {
  return STOCK_CSV_COLUMN_IDS.map((fieldId) => {
    if (fieldId === "symbol") {
      return stock.symbol != null ? String(stock.symbol).trim() : "";
    }
    if (fieldId === "earnings_date") {
      return formatStockEarningsDateForCsv(stock.earnings_date);
    }
    if (STOCK_CSV_TEXT_FIELDS.has(fieldId)) {
      return stock[fieldId] != null ? String(stock[fieldId]).trim() : "";
    }
    if (STOCK_CSV_PCT_FIELDS.has(fieldId)) {
      return formatStockPctForCsv(stock[fieldId]);
    }
    return formatStockNumericForCsv(stock[fieldId]);
  });
}

/**
 * @param {Record<string, unknown>[]} stocks
 * @returns {string}
 */
export function buildStockCsvExportText(stocks) {
  const rows = Array.isArray(stocks) ? stocks : [];
  const lines = [
    STOCK_CSV_HEADER_LINE,
    ...rows.map((stock) => stockRecordToCsvCells(stock).map(escapeCsvCell).join(",")),
  ];
  return lines.join("\r\n");
}
